import { EmptyState, cn } from '@vta/ui';

/**
 * Renders user-submitted bio text safely.
 *
 * Hard rules (PRD §19):
 *   - Never render HTML. The string is treated as plain text.
 *   - Newlines split into `<p>` blocks; consecutive blanks collapse.
 *   - No external link auto-detection (would require a regex over user input
 *     and a careful sanitiser; out of scope for V1).
 *
 * React's default JSX text node escaping handles the rest — no need for
 * `dangerouslySetInnerHTML` and explicitly no markdown parser.
 */
export interface TeamBioProps {
  bio: string | null | undefined;
  /** When false (e.g. drawer) we render the empty state inline rather than as a card. */
  card?: boolean;
  className?: string;
}

export function TeamBio({ bio, card = true, className }: TeamBioProps) {
  const trimmed = bio?.trim();
  if (!trimmed) {
    if (!card) {
      return (
        <p className={cn('text-sm text-fg-subtle', className)}>
          No team story yet. Story submissions are reviewed before publishing.
        </p>
      );
    }
    return (
      <EmptyState
        title="No team story yet"
        description="Stories are user-submitted and reviewed by an admin before publishing."
        className={className}
      />
    );
  }

  const paragraphs = trimmed
    .split(/\r?\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className={cn('flex flex-col gap-4 text-sm leading-relaxed text-fg-muted', className)}>
      {paragraphs.map((para, i) => (
        <p key={i} className="whitespace-pre-line">
          {para}
        </p>
      ))}
    </div>
  );
}
